import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CheckCircle2 } from "lucide-react";
import { BUSINESS } from "@/lib/business";
import { SERVICES } from "@/lib/services";
import { PhoneButton } from "./PhoneButton";

const schema = z.object({
  service: z.string().min(1, "Pick a service"),
  urgency: z.enum(["Emergency", "Today", "This week", "Just a quote"]),
  name: z.string().trim().min(2, "Your name").max(80),
  phone: z.string().trim().min(7, "Phone number").max(20),
  email: z.string().trim().email("Valid email").max(160).optional().or(z.literal("")),
  zip: z.string().trim().min(5, "Phoenix-area ZIP").max(10),
  address: z.string().trim().max(160).optional(),
  details: z.string().trim().max(800).optional(),
  website: z.string().max(0).optional(), // honeypot
});
type FormValues = z.infer<typeof schema>;

export function QuoteForm({ compact = false, defaultService }: { compact?: boolean; defaultService?: string }) {
  const [step, setStep] = useState(1);
  const [sent, setSent] = useState(false);
  const {
    register, handleSubmit, trigger, watch, formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { service: defaultService ?? "", urgency: "Today" },
  });

  async function next() {
    const fields: (keyof FormValues)[] = step === 1 ? ["service"] : ["urgency"];
    if (await trigger(fields)) setStep((s) => s + 1);
  }

  async function onSubmit(values: FormValues) {
    if (values.website) return; // honeypot
    try {
      await fetch("/api/public/quote-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: values.name,
          phone: values.phone,
          email: values.email || "",
          zip: values.zip,
          address: values.address || "",
          service: values.service,
          urgency: values.urgency,
          details: values.details || "",
        }),
      });
    } catch {
      // best-effort — the mailto fallback link is still shown on success screen
    }
    // @ts-expect-error optional analytics
    window.dataLayer?.push?.({ event: "quote_request", service: values.service, urgency: values.urgency });
    setSent(true);
  }

  if (sent) {
    return (
      <div className={`rounded-xl bg-card p-6 shadow-xl ring-1 ring-border ${compact ? "" : "lg:p-8"}`}>
        <div className="flex items-center gap-3 text-success">
          <CheckCircle2 className="h-7 w-7" />
          <h3 className="text-xl font-extrabold">Thanks — we got it.</h3>
        </div>
        <p className="mt-2 text-foreground">
          A dispatcher will call you within 15 minutes. For faster service right now:
        </p>
        <div className="mt-4"><PhoneButton size="lg" /></div>
        <p className="mt-4 text-sm">
          Love the work later?{" "}
          <a href={BUSINESS.reviewLink} className="text-primary font-semibold underline">
            Leave us a Google review →
          </a>
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`rounded-xl bg-card p-6 shadow-xl ring-1 ring-border ${compact ? "" : "lg:p-8"}`}
      aria-label="Request a free quote"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-extrabold text-secondary">Get a Free Quote</h3>
        <span className="text-xs font-bold text-muted-foreground">Step {step} of 3</span>
      </div>
      <div className="h-1.5 w-full rounded bg-muted overflow-hidden mb-5">
        <div className="h-full bg-primary transition-all" style={{ width: `${(step / 3) * 100}%` }} />
      </div>

      {/* Honeypot */}
      <input type="text" tabIndex={-1} autoComplete="off" {...register("website")} className="hidden" aria-hidden />

      {step === 1 && (
        <div className="space-y-3">
          <label htmlFor="quote-service" className="block text-sm font-semibold text-secondary">What do you need?</label>
          <select id="quote-service" {...register("service")} className="w-full rounded-md border border-input bg-background px-3 py-3 text-foreground">
            <option value="">Choose a service…</option>
            {SERVICES.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
            <option value="Other">Something else</option>
          </select>
          {errors.service && <p className="text-sm text-primary">{errors.service.message}</p>}
          <button type="button" onClick={next} className="w-full rounded-md bg-primary text-primary-foreground py-3 font-extrabold hover:bg-primary-dark">
            Next →
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <label className="block text-sm font-semibold text-secondary">How urgent?</label>
          <div className="grid grid-cols-2 gap-2">
          {(["Emergency","Today","This week","Just a quote"] as const).map((u) => (
              <label key={u} className={`cursor-pointer rounded-md border-2 p-3 text-sm font-semibold text-center transition-colors ${watch("urgency") === u ? "border-primary bg-primary/5 text-primary" : "border-border bg-muted/40 text-foreground hover:bg-muted"}`}>
                <input type="radio" value={u} {...register("urgency")} className="sr-only" />
                {u}
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setStep(1)} className="flex-1 rounded-md border border-border py-3 font-semibold">Back</button>
            <button type="button" onClick={next} className="flex-1 rounded-md bg-primary text-primary-foreground py-3 font-extrabold hover:bg-primary-dark">Next →</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-semibold text-secondary mb-1" htmlFor="name">Name</label>
            <input id="name" {...register("name")} className="w-full rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground px-3 py-2.5" />
            {errors.name && <p className="text-sm text-primary">{errors.name.message}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-secondary mb-1" htmlFor="phone">Phone</label>
              <input id="phone" type="tel" {...register("phone")} className="w-full rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground px-3 py-2.5" />
              {errors.phone && <p className="text-sm text-primary">{errors.phone.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-secondary mb-1" htmlFor="zip">ZIP</label>
              <input id="zip" {...register("zip")} className="w-full rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground px-3 py-2.5" />
              {errors.zip && <p className="text-sm text-primary">{errors.zip.message}</p>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-secondary mb-1" htmlFor="email">Email (optional)</label>
            <input id="email" type="email" {...register("email")} className="w-full rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground px-3 py-2.5" />
            {errors.email && <p className="text-sm text-primary">{errors.email.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-secondary mb-1" htmlFor="address">Address (optional)</label>
            <input id="address" {...register("address")} className="w-full rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground px-3 py-2.5" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-secondary mb-1" htmlFor="details">What's going on?</label>
            <textarea id="details" rows={3} {...register("details")} className="w-full rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground px-3 py-2.5" />
          </div>
          <p className="text-xs text-muted-foreground">Your info is private. No spam, ever.</p>
          <div className="flex gap-2">
            <button type="button" onClick={() => setStep(2)} className="flex-1 rounded-md border border-border py-3 font-semibold">Back</button>
            <button type="submit" disabled={isSubmitting} className="flex-[2] rounded-md bg-primary text-primary-foreground py-3 font-extrabold hover:bg-primary-dark disabled:opacity-60">
              {isSubmitting ? "Sending…" : "Get My Free Quote →"}
            </button>
          </div>
          <a href={`mailto:${BUSINESS.email}?subject=Quote%20request`} className="block text-center text-xs text-muted-foreground underline">
            Or email us directly
          </a>
        </div>
      )}
    </form>
  );
}